import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import {
  setOpportunityPage,
  setOpportunitiesPerPage,
  selectOpportunityFilters,
  defaultOrderBy,
  useSaveOpportunityFilters,
} from '@user/store/opportunity'
import { getSaveListViewPreferenceFunc } from '@user/store/userPreferences'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
} from '@user/store/userPreferences/userPreferencesGeneral'
import {
  GridRowSelectionModel,
  GridSortModel,
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
} from '@mui/x-data-grid-pro'
import {
  FieldState,
  getOrderByFunc,
  orderByToSortModel,
} from '@user/components/xGrid'
import { OppListProps } from './typings'
import { useBrowserHistoryData } from '@user/utils/useBrowserHistoryData'
import { opportunitySortConfig } from './opportunityListColumns'

const getOrderBy = getOrderByFunc(defaultOrderBy, opportunitySortConfig)
const saveListViewPreference = getSaveListViewPreferenceFunc('opp')

export const useActions = (props: OppListProps) => {
  const dispatch = useAppDispatch()
  const { setBrowserHistory } = useBrowserHistoryData()

  const handlePaginationModelChange = useCallback(
    (pageModel: GridPaginationModel) => {
      const { page, pageSize } = pageModel
      sessionStorage.setItem('pageNumber', (page + 1)?.toString())
      sessionStorage.setItem('pageSize', pageSize?.toString())
      setBrowserHistory()
      dispatch(setOpportunitiesPerPage(pageSize))
      dispatch(setOpportunityPage(page + 1))
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.OpportunityPageSize,
          value: String(pageSize),
        }),
      )
    },
    [dispatch, setBrowserHistory],
  )

  const { filterSet } = useSelector(selectOpportunityFilters)
  const saveFilters = useSaveOpportunityFilters()

  const sortModel = useMemo(
    () =>
      props.columns.length === 0
        ? undefined
        : orderByToSortModel(filterSet.orderBy, opportunitySortConfig),
    [filterSet.orderBy, props.columns.length],
  )

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      const newOrderBy = getOrderBy(sortModel)
      if (newOrderBy !== filterSet.orderBy)
        saveFilters({ ...filterSet, orderBy: newOrderBy })
    },
    [filterSet, saveFilters],
  )

  const onSaveColumnState = useCallback(
    (columnOrder: FieldState[], columns: GridColDef[]) => {
      const mappedData = columnOrder?.map((row: FieldState) => {
        const found = columns?.find(
          (findRow: GridColDef) => findRow?.field == row?.field,
        )
        if (found && found?.width) {
          return {
            ...row,
            width: found?.width?.toString(),
          }
        }
        return {
          ...row,
        }
      })
      saveListViewPreference(dispatch, mappedData)
    },
    [dispatch],
  )

  const selectionChangeCallback = useCallback(
    (selectionModel: GridRowSelectionModel) => {
      props.setFiltersDisabled(selectionModel.length > 0)
    },
    [props],
  )

  const onColumnWidthChange = useCallback(
    (
      columnData: GridColDef,
      columns: GridColDef[],
      columnVisibility: GridColumnVisibilityModel,
    ) => {
      const mapToFieldState: FieldState[] = [...columns]?.map(
        (row: GridColDef) => {
          if (row?.width) {
            return {
              field: row?.field,
              hide: !columnVisibility?.[row?.field],
              width: row?.width?.toString(),
            }
          }
          return {
            field: row?.field,
            hide: !columnVisibility?.[row?.field],
          }
        },
      )

      const foundColumn = mapToFieldState?.find(
        (row: FieldState) => row?.field == columnData?.field,
      )
      if (foundColumn) {
        const indexOf = mapToFieldState?.findIndex(
          (row) => row?.field == columnData?.field,
        )
        const newColumnObject = {
          ...foundColumn,
          width: columnData?.width?.toString(),
        }
        mapToFieldState.splice(indexOf, 1, newColumnObject)
        saveListViewPreference(dispatch, mapToFieldState)
      }
    },
    [dispatch],
  )

  return {
    handlePaginationModelChange,
    sortModel,
    onSortModelChange,
    onSaveColumnState,
    selectionChangeCallback,
    onColumnWidthChange,
  }
}
